'use client';

import * as React from 'react';
import { Button } from './Button';
import { cn } from '../utils/cn';

/**
 * Cookie-banner komponens — ePrivacy direktíva + NAIH 2020 iránymutatás.
 *
 * Granuláris opt-in (kategóriák szerint), elutasítás egy kattintással,
 * a választás `localStorage`-ban perzisztálva (`lectura-cookie-consent`).
 *
 * A szövegek a {@link CookieBannerProps} `texts` propján keresztül adhatók át —
 * a `docs/compliance-todo-legal.md` A5 szakaszában szerepel a DPO által
 * jóváhagyandó tartalom. Default magyar mock-szöveg; production-előkészítés
 * előtt DPO-aláírt verzióra cserélendő.
 */

export type CookieCategory = 'necessary' | 'statistics' | 'marketing';

export interface CookieConsent {
  necessary: true; // mindig true (jogszerű érdek)
  statistics: boolean;
  marketing: boolean;
  acceptedAt: string; // ISO timestamp
  version: number;
}

const STORAGE_KEY = 'lectura-cookie-consent';
const CONSENT_VERSION = 1;

export function loadConsent(): CookieConsent | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CookieConsent;
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveConsent(consent: CookieConsent): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  // Custom event a többi komponensnek (pl. Analytics provider)
  window.dispatchEvent(new CustomEvent('lectura-cookie-consent', { detail: consent }));
}

export function revokeConsent(): void {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new CustomEvent('lectura-cookie-consent', { detail: null }));
}

export interface CookieBannerTexts {
  title: string;
  description: string;
  privacyLinkLabel: string;
  privacyLinkHref: string;
  acceptAll: string;
  acceptNecessary: string;
  customize: string;
  save: string;
  categories: {
    necessary: { label: string; description: string };
    statistics: { label: string; description: string };
    marketing: { label: string; description: string };
  };
}

const DEFAULT_TEXTS: CookieBannerTexts = {
  title: 'Sütik használata',
  description:
    'Sütiket használunk a működéshez, statisztikák gyűjtéséhez és — az Ön engedélyével — '
    + 'a tartalom személyre szabásához. A „Csak szükségesek" gombbal a marketing és '
    + 'statisztikai cookie-k nem aktiválódnak.',
  privacyLinkLabel: 'Adatkezelési tájékoztató',
  privacyLinkHref: '/adatvedelem',
  acceptAll: 'Mindent elfogadok',
  acceptNecessary: 'Csak szükségesek',
  customize: 'Beállítások',
  save: 'Választás mentése',
  categories: {
    necessary: {
      label: 'Szükséges',
      description:
        'A bejelentkezéshez és a biztonsági funkciókhoz elengedhetetlen sütik. '
        + 'Mindig aktívak, nem kapcsolhatók ki.',
    },
    statistics: {
      label: 'Statisztikai',
      description:
        'Anonim adatok arról, hogy a látogatók hogyan használják az oldalt — '
        + 'segít a hibák felderítésében és a továbbfejlesztésben.',
    },
    marketing: {
      label: 'Marketing',
      description:
        'A tartalom személyre szabásához és a relevánsabb ajánlatokhoz. '
        + 'Külső szolgáltatóknak (lista az adatkezelési tájékoztatóban) is továbbít adatot.',
    },
  },
};

export interface CookieBannerProps {
  texts?: Partial<CookieBannerTexts>;
  className?: string;
  // Tesztelhetőség: ha true, a banner mindig megjelenik (ignorálja a localStorage-t)
  forceVisible?: boolean;
}

export function CookieBanner({ texts, className, forceVisible = false }: CookieBannerProps): React.ReactElement | null {
  const t: CookieBannerTexts = React.useMemo(
    () => ({
      ...DEFAULT_TEXTS,
      ...texts,
      categories: { ...DEFAULT_TEXTS.categories, ...(texts?.categories ?? {}) },
    }),
    [texts],
  );

  const [visible, setVisible] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);
  const [statistics, setStatistics] = React.useState(false);
  const [marketing, setMarketing] = React.useState(false);

  React.useEffect(() => {
    if (forceVisible) {
      setVisible(true);
      return;
    }
    const existing = loadConsent();
    setVisible(existing === null);
  }, [forceVisible]);

  const persist = React.useCallback((s: boolean, m: boolean) => {
    saveConsent({
      necessary: true,
      statistics: s,
      marketing: m,
      acceptedAt: new Date().toISOString(),
      version: CONSENT_VERSION,
    });
    setVisible(false);
  }, []);

  const acceptAll = () => persist(true, true);
  const acceptNecessary = () => persist(false, false);
  const saveCustom = () => persist(statistics, marketing);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label={t.title}
      aria-modal="false"
      className={cn(
        'fixed bottom-4 left-4 right-4 z-50 mx-auto max-w-3xl',
        'rounded-2xl bg-white p-6 shadow-2xl ring-1 ring-black/5',
        'sm:left-auto sm:right-4 sm:bottom-4',
        className,
      )}
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <h2 className="font-display text-lg font-semibold text-brand-900">{t.title}</h2>
      </div>

      <p className="mb-4 text-sm text-[var(--color-foreground-muted)]">
        {t.description}{' '}
        <a href={t.privacyLinkHref} className="underline hover:text-brand-700">
          {t.privacyLinkLabel}
        </a>
      </p>

      {expanded ? (
        <fieldset className="mb-4 flex flex-col gap-3">
          <CategoryRow
            label={t.categories.necessary.label}
            description={t.categories.necessary.description}
            checked
            disabled
            onChange={() => {}}
          />
          <CategoryRow
            label={t.categories.statistics.label}
            description={t.categories.statistics.description}
            checked={statistics}
            onChange={setStatistics}
          />
          <CategoryRow
            label={t.categories.marketing.label}
            description={t.categories.marketing.description}
            checked={marketing}
            onChange={setMarketing}
          />
        </fieldset>
      ) : null}

      <div className="flex flex-wrap items-center justify-end gap-2">
        {expanded ? (
          <Button variant="primary" onClick={saveCustom}>
            {t.save}
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={() => setExpanded(true)}>
              {t.customize}
            </Button>
            <Button variant="secondary" onClick={acceptNecessary}>
              {t.acceptNecessary}
            </Button>
            <Button variant="primary" onClick={acceptAll}>
              {t.acceptAll}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

interface CategoryRowProps {
  label: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (value: boolean) => void;
}

function CategoryRow({ label, description, checked, disabled, onChange }: CategoryRowProps): React.ReactElement {
  const id = React.useId();
  return (
    <label htmlFor={id} className="flex cursor-pointer items-start gap-3 rounded-lg p-3 hover:bg-brand-50/50">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 size-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500 disabled:opacity-60"
      />
      <span className="flex-1">
        <span className="block text-sm font-medium text-brand-900">{label}</span>
        <span className="block text-xs text-[var(--color-foreground-muted)]">{description}</span>
      </span>
    </label>
  );
}
